import { useTranslation } from "react-i18next";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import QuoteCarousel from "@/components/QuoteCarousel";
import { motion, useReducedMotion } from "framer-motion";
import { fadeUp, fadeIn, staggerContainer, staggerItem, instant } from "@/lib/animations";

const SAGE = "#A8B5A2";
const TERRACOTTA = "#D9B8A3";

export default function ImpactPage() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  const fadeUpV  = reduce ? instant : fadeUp;
  const fadeInV  = reduce ? instant : fadeIn;
  const staggerC = reduce ? instant : staggerContainer;
  const staggerI = reduce ? instant : staggerItem;

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))", fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
      <SiteNav />

      {/* Hero */}
      <section style={{
        position: "relative",
        padding: "100px 20px 80px",
        overflow: "hidden",
        background: "#0a0a0a",
      }}>
        <img
          src="https://images.unsplash.com/photo-1448375240586-882707db888b?w=1600&q=80&fit=crop&auto=format"
          alt="" aria-hidden loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.18, filter: "grayscale(0.15)", pointerEvents: "none" }}
        />
        <div aria-hidden style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "linear-gradient(to bottom, #0a0a0a 0%, transparent 28%, transparent 72%, #0a0a0a 100%)",
        }} />
        <div aria-hidden style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(168,181,162,0.07) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />
        <svg aria-hidden style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}>
          <circle cx="18%" cy="55%" r="220" fill="none" stroke={SAGE} strokeWidth="0.7" opacity="0.07" />
          <circle cx="85%" cy="40%" r="140" fill="none" stroke={TERRACOTTA} strokeWidth="0.5" opacity="0.06" />
        </svg>

        <motion.div
          variants={staggerC}
          initial="hidden"
          animate="visible"
          style={{ position: "relative", maxWidth: "680px", margin: "0 auto", textAlign: "center" }}
        >
          <motion.p variants={fadeInV} style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: SAGE, marginBottom: "20px" }}>
            {t("impact.eyebrow")}
          </motion.p>
          <motion.h1 variants={fadeUpV} style={{ fontSize: "clamp(32px, 5vw, 58px)", fontWeight: 200, color: "#ffffff", letterSpacing: "-0.03em", marginBottom: "24px", lineHeight: 1.1 }}>
            {t("impact.subheadline")}
          </motion.h1>
          <motion.p variants={fadeUpV} style={{ fontSize: "17px", fontWeight: 300, color: "rgba(255,255,255,0.50)", lineHeight: 1.8, maxWidth: "540px", margin: "0 auto" }}>
            {t("impact.body")}
          </motion.p>
        </motion.div>
      </section>

      {/* Mission statement */}
      <section style={{ padding: "80px 20px", maxWidth: "800px", margin: "0 auto" }}>
        <motion.div
          variants={staggerC}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "32px" }}
        >
          {[
            { num: "100%", label: "Goes to debt relief", sub: "Every dollar donated goes directly to eliminating real debt. Zero overhead taken from donations." },
            { num: "501(c)(3)", label: "Nonprofit status", sub: "Donations are tax-deductible. We operate with full transparency and accountability." },
            { num: "Real debt", label: "Verified and settled", sub: "We work with creditors to eliminate actual debt balances on behalf of recipients." },
            { num: "Community", label: "Powered by donors", sub: "Ordinary people pool their generosity to create extraordinary change in their neighbours' lives." },
          ].map(({ num, label, sub }) => (
            <motion.div
              key={label}
              variants={staggerI}
              className="glass-card"
              style={{
                padding: "32px 28px",
                borderRadius: "16px",
                cursor: "default",
              }}
            >
              <div style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 200, color: SAGE, letterSpacing: "-0.03em", marginBottom: "6px" }}>{num}</div>
              <div style={{ fontSize: "13px", fontWeight: 400, color: "#0a0a0a", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: "12px" }}>{label}</div>
              <p style={{ fontSize: "14px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.7, margin: 0 }}>{sub}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* How debt destroys lives */}
      <section style={{ position: "relative", padding: "80px 20px", background: "#0a0a0a", overflow: "hidden" }}>
        <div aria-hidden style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse 60% 50% at 80% 40%, rgba(217,184,163,0.06) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />
        <motion.div
          variants={staggerC}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          style={{ position: "relative", maxWidth: "760px", margin: "0 auto" }}
        >
          <motion.p variants={fadeInV} style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: TERRACOTTA, marginBottom: "16px" }}>
            The problem
          </motion.p>
          <motion.h2 variants={fadeUpV} style={{ fontSize: "clamp(26px, 4vw, 42px)", fontWeight: 200, color: "#ffffff", letterSpacing: "-0.03em", marginBottom: "24px", lineHeight: 1.2 }}>
            Medical bills. Student loans. A single crisis can take decades to survive.
          </motion.h2>
          <motion.p variants={fadeUpV} style={{ fontSize: "16px", fontWeight: 300, color: "rgba(255,255,255,0.50)", lineHeight: 1.85, marginBottom: "40px" }}>
            In the United States, over 100 million people carry medical debt. One unexpected diagnosis — cancer, a car accident, a premature birth — can trigger a cascade that costs families their homes, their credit, their futures. Student debt now exceeds $1.7 trillion. Consumer debt traps people in cycles that seem impossible to escape.
          </motion.p>
          <motion.p variants={fadeUpV} style={{ fontSize: "16px", fontWeight: 300, color: "rgba(255,255,255,0.50)", lineHeight: 1.85 }}>
            DebtBridge was built on one conviction: that communities have the collective power to break those cycles — if we make it simple enough to act together.
          </motion.p>
        </motion.div>
      </section>

      {/* Quote / testimonial strip */}
      <QuoteCarousel />

      <SiteFooter />
    </div>
  );
}
